import { usePermissionsControllerGetMyPermissions } from '@/generated/api/permissions.ts';
import type { PermissionRequest } from '@pac/auth-roles';

export type PermissionRecord = Record<string, string[]>;
export type { PermissionRequest };

export function hasPermissions(
  userPermissions: PermissionRecord | undefined,
  permission: PermissionRequest,
) {
  if (!userPermissions) return false;

  for (const resource of Object.keys(
    permission,
  ) as (keyof PermissionRequest)[]) {
    const requiredActions = permission[resource] ?? [];
    const allowedActions = userPermissions[resource] ?? [];

    for (const action of requiredActions) {
      if (!allowedActions.includes(action)) {
        return false;
      }
    }
  }

  return true;
}

export default function usePermissions({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return usePermissionsControllerGetMyPermissions<PermissionRecord>({
    query: {
      enabled,
      select: normalizePermissionsResponse,
      staleTime: 'static',
    },
  });
}

function normalizePermissionsResponse(response: unknown): PermissionRecord {
  if (
    response !== null &&
    typeof response === 'object' &&
    'permissions' in response
  ) {
    const permissions = (response as { permissions?: unknown }).permissions;
    return isPermissionRecord(permissions) ? permissions : {};
  }

  return isPermissionRecord(response) ? response : {};
}

function isPermissionRecord(value: unknown): value is PermissionRecord {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every(
    (actions) =>
      Array.isArray(actions) &&
      actions.every((action) => typeof action === 'string'),
  );
}
